import axios from 'axios';

// In production, Apollo API is served via the same domain at /apollo path prefix.
// In dev, Vite proxies /apollo/api → localhost:4200/api.
const APOLLO_URL = import.meta.env.VITE_APOLLO_URL || '/apollo';

const apolloApi = axios.create({
  baseURL: APOLLO_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

apolloApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred';
    return Promise.reject(new Error(message));
  }
);

// Brokers API
export const brokersAPI = {
  getAll: () => apolloApi.get('/api/brokers'),
  getById: (id) => apolloApi.get(`/api/brokers/${id}`),
  create: ({ name, autonomy, config, workflow } = {}) => apolloApi.post('/api/brokers', { name, autonomy, config, workflow }),
  update: (id, data) => apolloApi.put(`/api/brokers/${id}`, data),
  updateStatus: (id, status) => apolloApi.patch(`/api/brokers/${id}/status`, { status }),
  saveWorkflow: (id, workflow) => apolloApi.patch(`/api/brokers/${id}/workflow`, { workflow }),
  delete: (id) => apolloApi.delete(`/api/brokers/${id}`),
};

// Sessions API
export const sessionsAPI = {
  getAll: (brokerId) => apolloApi.get('/api/sessions', { params: { brokerId } }),
  create: (brokerId, title) => apolloApi.post('/api/sessions', { brokerId, title }),
  update: (id, data) => apolloApi.patch(`/api/sessions/${id}`, data),
  archive: (id) => apolloApi.patch(`/api/sessions/${id}/archive`),
  delete: (id) => apolloApi.delete(`/api/sessions/${id}`),
};

// Messages API
export const messagesAPI = {
  getAll: (sessionId, params = {}) => apolloApi.get('/api/messages', { params: { sessionId, ...params } }),
  getAllByBroker: (brokerId, params = {}) => apolloApi.get('/api/messages', { params: { brokerId, ...params } }),
  send: (brokerId, sessionId, content) => apolloApi.post('/api/messages', { brokerId, sessionId, content }),
  clear: (sessionId) => apolloApi.delete('/api/messages', { params: { sessionId } }),

  /**
   * Send a message with streaming SSE response.
   * @param {string} brokerId
   * @param {string} sessionId
   * @param {string} content
   * @param {object} context - { strategies, symbols }
   * @param {object} callbacks - { onToken, onUserMessage, onDone, onError, onSession }
   * @returns {Promise<void>}
   */
  async stream(brokerId, sessionId, content, context = {}, { onToken, onUserMessage, onDone, onError, onSession, onToolResult } = {}) {
    const res = await fetch(`${APOLLO_URL}/api/messages/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ brokerId, sessionId, content, context }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Stream failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7);
        } else if (line.startsWith('data: ')) {
          const raw = line.slice(6);
          try {
            const data = JSON.parse(raw);
            if (currentEvent === 'session' && onSession) onSession(data);
            else if (currentEvent === 'token' && onToken) onToken(data);
            else if (currentEvent === 'tool_result' && onToolResult) onToolResult(data);
            else if (currentEvent === 'user_message' && onUserMessage) onUserMessage(data);
            else if (currentEvent === 'done' && onDone) onDone(data);
            else if (currentEvent === 'error' && onError) onError(data);
          } catch { /* skip malformed JSON */ }
        }
      }
    }
  },
};

// Actions API
export const actionsAPI = {
  getAll: (brokerId, params = {}) => apolloApi.get('/api/actions', { params: { brokerId, ...params } }),
  getById: (id) => apolloApi.get(`/api/actions/${id}`),
  approve: (id, { payloadOverrides, executeNow = true } = {}) =>
    apolloApi.patch(`/api/actions/${id}/approve`, { payloadOverrides, executeNow }),
  execute: (id, payloadOverrides) =>
    apolloApi.patch(`/api/actions/${id}/execute`, { payloadOverrides }),
  reject: (id) => apolloApi.patch(`/api/actions/${id}/reject`),
};

export default apolloApi;
